/**
 * QuestionHeader.tsx
 * Question header with metadata and flag button
 */

import { useTranslation } from 'react-i18next';
import { Flag } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';
import { getLocalizedSubject } from '@/utils/subjectLocalization';

interface Props {
  questionNumber: number;
  totalQuestions: number;
  subject: string;
  questionType: string;
  points: number;
  isFlagged: boolean;
  onToggleFlag: () => void;
}

export default function QuestionHeader({
  questionNumber,
  totalQuestions,
  subject,
  questionType,
  points,
  isFlagged,
  onToggleFlag,
}: Props) {
  const { t } = useTranslation();
  const localizedSubject = getLocalizedSubject(subject, t);

  return (
    <div className='flex items-start sm:items-center justify-between pb-4 lg:pb-5 border-b border-border mb-4 lg:mb-6 gap-4'>
      <div
        className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs lg:text-sm min-w-0 flex-1'
        data-tour='question-title'
      >
        <span className='text-lg lg:text-xl font-bold text-foreground flex-shrink-0'>
          {t('landing.questions.header.questionOf', {
            current: questionNumber,
            total: totalQuestions,
          })}
        </span>
        <div className='flex items-center gap-2 text-muted-foreground flex-wrap'>
          <span className='hidden sm:inline text-muted-foreground dark:text-gray-500'>•</span>
          <span className='truncate'>{localizedSubject}</span>
          <span className='text-muted-foreground dark:text-gray-500'>•</span>
          <span className='truncate'>{questionType}</span>
          {/* Temporarily commented out due to bug showing 0 points */}
          {/* <span className='text-muted-foreground dark:text-gray-500'>•</span>
          <span className='flex-shrink-0'>{t('landing.questions.header.points', { points })}</span> */}
        </div>
      </div>

      <Button
        variant='ghost'
        size='sm'
        onClick={onToggleFlag}
        className={cn(
          'gap-1 lg:gap-2 transition-all duration-300 border border-transparent hover:border-primary/30 flex-shrink-0 text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-2',
          isFlagged
            ? 'text-primary bg-primary/10 border-primary/40 dark:border-primary'
            : 'text-muted-foreground hover:text-primary  hover:bg-primary/10 dark:hover:bg-primary/20/10'
        )}
        data-tour='flag'
      >
        <Flag className='w-3 h-3 lg:w-4 lg:h-4' />
        <span className='hidden sm:inline'>
          {isFlagged ? t('landing.questions.header.flagged') : t('landing.questions.header.flag')}
        </span>
        <span className='sm:hidden'>
          {isFlagged
            ? t('landing.questions.header.flaggedShort')
            : t('landing.questions.header.flag')}
        </span>
      </Button>
    </div>
  );
}
